import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import dpFilters from './dpFilters.json';

type FilterConfigKey = keyof typeof dpFilters;

type FilterKey =
    | 'market'
    | 'report'
    | 'sector'
    | 'gdo'
    | 'gdo_status'
    | 'program'
    | 'project'
    | 'program_status';

type FilterDef = {
    label: string;
    defaultValue: string;
    options: string[];
    minWidth?: number;
};

const FILTER_DEFS: Record<FilterKey, FilterDef> = {
    market: {
        label: 'Market',
        defaultValue: 'All',
        options: ['All', 'BNL', 'FRANCE'],
        minWidth: 160
    },
    report: {
        label: 'Reports',
        defaultValue: 'All',
        options: ['All', 'iS&OE', 'iS&OP'],
        minWidth: 160
    },
    gdo: {
        label: 'GDO',
        defaultValue: 'All GDO',
        options: ['All GDO', 'GDO-3', 'GDO-5'],
        minWidth: 160
    },
    gdo_status: {
        label: 'GDO Status',
        defaultValue: 'All Status',
        options: ['All Status', 'SLA Met', 'SLA Missed', 'In Progress'],
        minWidth: 180
    },
    sector: { label: 'Sector', defaultValue: 'All', options: ['All'], minWidth: 160 },
    program: { label: 'Program', defaultValue: 'All', options: ['All'], minWidth: 160 },
    project: { label: 'Project', defaultValue: 'All', options: ['All'], minWidth: 160 },
    program_status: { label: 'Program Status', defaultValue: 'All', options: ['All'], minWidth: 180 }
};

export default function IBPFilters({
    filterConfigKey = 'ibp',
    filters,
    defaultValues,
    onChange
}: {
    filterConfigKey?: FilterConfigKey;
    filters?: FilterKey[];
    defaultValues?: Partial<Record<FilterKey, string>>;
    onChange?: (values: Record<string, string>) => void;
}) {
    // `filters` is often passed inline (new array each render).
    // Stabilize by value so we don't recompute keys / defaults endlessly.
    const filtersKey = useMemo(() => JSON.stringify(filters ?? []), [filters]);

    const activeFilterKeys = useMemo<FilterKey[]>(() => {
        if (filters && filters.length > 0) {
            return filters;
        }
        const cfg = dpFilters[filterConfigKey] ?? dpFilters.generic;
        return (cfg.filters ?? []) as FilterKey[];
    }, [filtersKey, filterConfigKey]);

    const ibpFilterLabelSx = {
        '&&': { color: '#555 !important' },
        '&&.Mui-focused': { color: '#555 !important' },
        '&&.MuiInputLabel-shrink': { color: '#555 !important' }
    };

    const ibpFilterSelectSx = {
        '&&': {
            backgroundColor: '#F3F8FF !important',
            borderRadius: '5px',
            boxShadow: 'none !important'
        },
        '&& .MuiSelect-select': {
            color: '#222 !important',
            WebkitTextFillColor: '#222 !important'
        },
        '&& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#9e9e9e !important',
            borderWidth: '1px !important'
        },
        '&&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#9e9e9e !important',
            borderWidth: '1px !important'
        },
        '&&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#bdbdbd !important',
            borderWidth: '1px !important'
        },
        '&&:active .MuiOutlinedInput-notchedOutline': {
            borderColor: '#bdbdbd !important',
            borderWidth: '1px !important'
        },
        '&&.Mui-focused': { boxShadow: 'none !important' },
        '&& .MuiSvgIcon-root': { color: '#555 !important' }
    };

    const ibpFilterMenuProps = {
        PaperProps: {
            sx: {
                backgroundColor: '#1a3a5c !important',
                color: '#fff !important',
                borderRadius: '6px',
                border: '1px solid rgba(255,255,255,0.25)',
                mt: 1,
                zIndex: 9999,
                '& .MuiList-root': {
                    backgroundColor: '#1a3a5c !important',
                    padding: '4px 0'
                },
                '& .MuiMenuItem-root': {
                    fontSize: 14,
                    color: '#fff !important',
                    backgroundColor: '#1a3a5c !important'
                },
                '& .MuiMenuItem-root:hover': {
                    backgroundColor: '#005b99 !important'
                },
                '& .MuiMenuItem-root.Mui-selected': {
                    backgroundColor: '#005b99 !important'
                },
                '& .MuiMenuItem-root.Mui-selected:hover': {
                    backgroundColor: '#004b80 !important'
                }
            }
        },
        disablePortal: false,
        style: { zIndex: 9999 }
    } as const;

    // `defaultValues` is often passed inline (new object each render).
    // Stabilize it by value so we don't keep resetting state and causing re-render loops.
    const defaultValuesKey = useMemo(() => JSON.stringify(defaultValues ?? {}), [defaultValues]);

    const buildDefaults = useMemo(() => {
        const initial: Record<string, string> = {};
        activeFilterKeys.forEach((k) => {
            initial[k] = defaultValues?.[k] ?? FILTER_DEFS[k]?.defaultValue ?? 'All';
        });
        return initial;
    }, [activeFilterKeys, defaultValuesKey]);

    const [values, setValues] = useState<Record<string, string>>(buildDefaults);

    const onChangeRef = useRef<typeof onChange>(onChange);

    useEffect(() => {
        onChangeRef.current = onChange;
    }, [onChange]);

    useEffect(() => {
        setValues(buildDefaults);
    }, [buildDefaults]);

    useEffect(() => {
        onChangeRef.current?.(values);
    }, [values]);

    const handleChange = (key: FilterKey) => (e: SelectChangeEvent) => {
        const next = { ...values, [key]: e.target.value };
        setValues(next);
    };

    return (
        <Box
            sx={{
                display: 'flex',
                gap: 1.5,
                alignItems: 'center',
                flexWrap: 'nowrap',
                ml: 1,
                minWidth: 0,
                maxWidth: '100%',
                overflowX: 'auto',
                overflowY: 'visible',
                pt: 0.75,
                pb: 0.25,
                '&::-webkit-scrollbar': { height: 6 }
            }}
        >
            {activeFilterKeys
                .filter((key) => Boolean(FILTER_DEFS[key]))
                .map((key) => {
                    const def = FILTER_DEFS[key];
                    const labelId = `${key}-select-label`;
                    const selectId = `${key}-select`;

                    return (
                        <FormControl
                            key={key}
                            size="small"
                            sx={{
                                minWidth: { xs: 140, sm: def.minWidth ?? 160 },
                                flex: '0 0 auto',
                                flexShrink: 0
                            }}
                        >
                            <InputLabel id={labelId} sx={ibpFilterLabelSx}>
                                {def.label}
                            </InputLabel>
                            <Select
                                labelId={labelId}
                                id={selectId}
                                value={values[key] ?? def.defaultValue}
                                label={def.label}
                                onChange={handleChange(key)}
                                sx={ibpFilterSelectSx}
                                MenuProps={ibpFilterMenuProps}
                            >
                                {def.options.map((opt) => (
                                    <MenuItem key={opt} value={opt}>
                                        {opt}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    );
                })}
        </Box>
    );
}
